// client/src/components/recursos/RecursoForm.js - VERSÃO COMPLETA CORRIGIDA (APENAS ORDEM CORRIGIDA)
import React, { useState, useEffect, useCallback } from 'react';
import { useMutateRecursos, useRecursoPaiDisponiveis, useRecursosDisponiveisAgrupamento } from '../../hooks/useRecursos';
import { useUnidades } from '../../hooks/useUnidades';
import { useCategoriasParaFormulario } from '../../hooks/useCategorias';
import useAlert from '../../hooks/useAlert';
import { useTenantI18n } from '../../hooks/useAccounts';
import { getCurrencySymbol } from '../../utils/formatters';
import {
    XCircle,
    PlusCircle,
    Trash2,
    Globe,
    Building,
    Cpu,
    Layers,
    MapPin,
    Folder,
    Tag,
    Image,
    Users,
    Link
} from 'lucide-react';
import PropTypes from 'prop-types';
import { FormStepper, FormStepNavigation } from '../shared/FormStepper';

const VALORES_DISPONIBILIDADE = [
    'INTERNO_SERVICOS', 
    'INTERNO_LOCACAO', 
    'EXTERNO_SERVICOS', 
    'EXTERNO_LOCACAO'
];

const TIPOS_RECURSO = [
    { value: 'Sala', label: 'Sala/Consultório', icon: '🏢' },
    { value: 'Equipamento', label: 'Equipamento', icon: '🔧' },
    { value: 'Maca ou Cadeira', label: 'Maca ou Cadeira', icon: '🛏️' },
    { value: 'Veiculo', label: 'Veículo', icon: '🚗' },
    { value: 'Outros', label: 'Outros', icon: '📦' }
];

const DEFAULT_PLACEHOLDER_IMAGE = '/default-recurso.png';

// ✅ FUNÇÃO CRÍTICA: Extrai o ID de forma segura
const extractId = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
        if (item.$oid) return String(item.$oid);
        if (item._id) return String(item._id);
        if (item.id) return String(item.id);
    }
    return String(item);
};

const extrairImagemUrlDoRecurso = (recurso) => {
    if (!recurso) return null;

    if (recurso.imagemUrl && recurso.imagemUrl !== DEFAULT_PLACEHOLDER_IMAGE && !recurso.imagemUrl.includes('data:image/svg')) {
        return recurso.imagemUrl;
    }

    if (recurso.imagemPrincipal && recurso.imagemPrincipal.refId) {
        const principalRefId = recurso.imagemPrincipal.refId.toString();
        
        if (recurso.imagens && Array.isArray(recurso.imagens)) {
            const imagemEncontrada = recurso.imagens.find(img => {
                if (!img) return false;
                const imgId = img._id || img.id;
                return imgId && imgId.toString() === principalRefId;
            });
            
            if (imagemEncontrada) {
                const url = imagemEncontrada.arquivo || imagemEncontrada.url || imagemEncontrada.caminhoS3 || imagemEncontrada.path || imagemEncontrada.imagemUrl;
                if (url) return url;
            }
        }
        
        if (recurso.imagensDaGaleria && Array.isArray(recurso.imagensDaGaleria)) {
            const imagemEncontrada = recurso.imagensDaGaleria.find(img => {
                if (!img) return false;
                const imgId = img._id || img.id;
                return imgId && imgId.toString() === principalRefId;
            });
            
            if (imagemEncontrada) {
                const url = imagemEncontrada.arquivo || imagemEncontrada.url || imagemEncontrada.caminhoS3 || imagemEncontrada.path || imagemEncontrada.imagemUrl;
                if (url) return url;
            }
        }
    }
    
    if (recurso.imagens && recurso.imagens.length > 0) {
        const primeiraImagem = recurso.imagens[0];
        if (primeiraImagem && typeof primeiraImagem === 'object') {
            const url = primeiraImagem.arquivo || primeiraImagem.url || primeiraImagem.caminhoS3 || primeiraImagem.path || primeiraImagem.imagemUrl;
            if (url) return url;
        }
    }
    
    if (recurso.imagensDaGaleria && recurso.imagensDaGaleria.length > 0) {
        const primeiraImagem = recurso.imagensDaGaleria[0];
        if (primeiraImagem && typeof primeiraImagem === 'object') {
            const url = primeiraImagem.arquivo || primeiraImagem.url || primeiraImagem.caminhoS3 || primeiraImagem.path || primeiraImagem.imagemUrl;
            if (url) return url;
        }
    }
    
    return null;
};

const processarImagensParaPreview = (recurso) => {
    if (!recurso) return null;
    const imagemUrlExtraida = extrairImagemUrlDoRecurso(recurso);
    if (imagemUrlExtraida) return imagemUrlExtraida;
    return null;
};

const initialState = {
    nome: '',
    tipo: 'Equipamento',
    descricao: '',
    status: 'Ativo',
    tags: [],
    temAgendaPropria: false,
    dependencias: [],
    disponivelPara: ['INTERNO_SERVICOS', 'INTERNO_LOCACAO'],
    tipoDePrecificacao: 'Por Hora',
    valorPorHora: 0,
    pacotesDePreco: [],
    unidade: '',
    recursoPai: '',
    unidadesPermitidas: [],
    categoria: '',
    imagemPrincipal: null,
    imagemUrl: '',
    isGrupoDeRecursos: false,
    recursosDoGrupo: [],
    grupoPertencente: '',
    metadata: {
        tipoItem: ['interno'],
        categoriaItem: 'locacao',
        disponivelOnline: false,
        movel: false,
        capacidade: 1,
        caracteristicas: []
    }
};

const RecursoForm = ({ onCloseForm, onSaveSuccess, current, accountId }) => {
    // ✅ ACHADO: rótulos abaixo eram fixos em "(R$)", sem ler
    // configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();
    const currencySymbol = getCurrencySymbol(currency, locale);

    // ✅ Correção para ResizeObserver
    useEffect(() => {
        const errorHandler = (e) => {
            if (e.message && e.message.includes('ResizeObserver loop')) {
                const err = e;
                err.stopImmediatePropagation();
            }
        };
        window.addEventListener('error', errorHandler);
        return () => window.removeEventListener('error', errorHandler);
    }, []);

    const { addRecurso, updateRecurso } = useMutateRecursos(accountId);
    const { setAlert } = useAlert();
    const { data: unidadesData, isLoading: loadingUnidades, error: unidadesError } = useUnidades(accountId);
    
    const { data: categoriasData, isLoading: loadingCategorias, error: categoriasError } = useCategoriasParaFormulario(accountId);
    
    // ✅ ESTADO - Declarado PRIMEIRO antes de qualquer hook que dependa dele
    const [recurso, setRecurso] = useState(initialState);
    const [currentStep, setCurrentStep] = useState(1);
    const [imagemPreview, setImagemPreview] = useState(DEFAULT_PLACEHOLDER_IMAGE);
    const [errors, setErrors] = useState({});
    const [imageUpdateTrigger, setImageUpdateTrigger] = useState(0);
    const [novaCaracteristica, setNovaCaracteristica] = useState('');
    const [novaTag, setNovaTag] = useState('');
    const [recursoSelecionadoParaGrupo, setRecursoSelecionadoParaGrupo] = useState('');

    const isEditing = !!current;
    const loading = addRecurso.isPending || updateRecurso.isPending;

    // ✅ HOOKS que dependem do estado recurso - chamados APÓS a declaração do estado
    const { data: recursosDisponiveisData, isLoading: loadingRecursosDisponiveis } = useRecursosDisponiveisAgrupamento(
        accountId,
        recurso?.tipo,
        current?._id
    );
    
    const { data: recursosPaiData, isLoading: isLoadingRecursosPai } = useRecursoPaiDisponiveis(
        accountId, 
        recurso?.tipo,
        recurso?.unidade
    );

    const unidades = Array.isArray(unidadesData?.data) ? unidadesData.data : 
                     Array.isArray(unidadesData) ? unidadesData : [];

    const categorias = Array.isArray(categoriasData) ? categoriasData.filter(categoria => 
        categoria.tipo === 'recurso' || categoria.tipo === 'geral'
    ) : [];

    const recursosDisponiveis = recursosDisponiveisData?.data || [];
    const recursosPaiDisponiveis = recursosPaiData?.data || [];

    // A aba "Preços" só existe quando o recurso está disponível para locação —
    // por isso o número do passo de cada aba é calculado dinamicamente em vez
    // de fixo, e usado tanto pelo FormStepper quanto pelos saltos de validação
    // do onSubmit (ex: "volta pra aba com erro").
    const showPrecosTab = recurso.disponivelPara.some(disponivel => disponivel.includes('LOCACAO')) && !recurso.isGrupoDeRecursos;
    const STEP_LABELS = ['Dados Básicos', 'Vínculos', ...(showPrecosTab ? ['Preços'] : []), 'Configurações'];
    const STEP_IDS = ['basicos', 'vinculos', ...(showPrecosTab ? ['precos'] : []), 'configuracoes'];
    const goToTab = (tabId) => {
        const index = STEP_IDS.indexOf(tabId);
        if (index >= 0) setCurrentStep(index + 1);
    };

    const inferirTipoDuracao = (tipoDuracao, nome, descricao) => {
        if (tipoDuracao && ['diaria', 'semanal', 'mensal'].includes(tipoDuracao)) return tipoDuracao;
        const texto = ((nome || '') + ' ' + (descricao || '')).toLowerCase();
        if (/\bdi[aá]ria[s]?\b/.test(texto)) return 'diaria';
        if (/\bsemanal\b|\bsemana[s]?\b/.test(texto)) return 'semanal';
        if (/\bmensal\b|\bm[eê]s\b|\bmeses\b/.test(texto)) return 'mensal';
        return tipoDuracao || 'horas';
    };

    useEffect(() => {
        const recursoParaProcessar = current || recurso;
        const novaImagemPreview = processarImagensParaPreview(recursoParaProcessar);

        if (novaImagemPreview !== imagemPreview) {
            setImagemPreview(novaImagemPreview || DEFAULT_PLACEHOLDER_IMAGE);
        }
    }, [current, recurso, imageUpdateTrigger, imagemPreview]);

    useEffect(() => {
        if (current) {
            const normalizarDisponivelPara = (disponivelPara) => {
                if (!Array.isArray(disponivelPara)) {
                    return ['INTERNO_SERVICOS', 'INTERNO_LOCACAO'];
                }

                const valoresValidos = disponivelPara.filter(valor =>
                    VALORES_DISPONIBILIDADE.includes(valor)
                );

                return valoresValidos.length > 0 ? valoresValidos : ['INTERNO_SERVICOS', 'INTERNO_LOCACAO'];
            };

            const normalizarPacotes = (pacotes) => {
                if (!Array.isArray(pacotes)) return [];

                return pacotes.map(pacote => ({
                    nome: pacote.nome || '',
                    tipoDuracao: inferirTipoDuracao(pacote.tipoDuracao, pacote.nome, pacote.descricao),
                    quantidade: pacote.quantidade && pacote.quantidade > 0 ? Number(pacote.quantidade) : 1,
                    valor: pacote.valor && pacote.valor > 0 ? Number(pacote.valor) : 0,
                    descontoPercentual: pacote.descontoPercentual ? Number(pacote.descontoPercentual) : 0,
                    ativo: pacote.ativo !== false,
                    horaInicio: pacote.horaInicio || '',
                    horaFim: pacote.horaFim || ''
                }));
            };

            let recursoPaiValue = extractId(current.recursoPai);

            const recursoAtualizado = {
                ...initialState,
                ...current,
                imagemUrl: extrairImagemUrlDoRecurso(current) || current.imagemUrl || '',
                valorPorHora: current.valorPorHora !== undefined && current.valorPorHora !== null ? Number(current.valorPorHora) : 0,
                tipoDePrecificacao: current.tipoDePrecificacao || 'Por Hora',
                pacotesDePreco: normalizarPacotes(current.pacotesDePreco),
                status: current.status || 'Ativo',
                tipo: current.tipo || 'Equipamento',
                temAgendaPropria: current.temAgendaPropria || false,
                dependencias: Array.isArray(current.dependencias) ? current.dependencias.map(dependencia => extractId(dependencia)) : [],
                disponivelPara: normalizarDisponivelPara(current.disponivelPara),
                unidade: extractId(current.unidade) || '',
                recursoPai: recursoPaiValue,
                categoria: extractId(current.categoria) || '',
                unidadesPermitidas: Array.isArray(current.unidadesPermitidas) ?
                    current.unidadesPermitidas.map(unidade => extractId(unidade)) : [],
                tags: Array.isArray(current.tags) ? current.tags : [],
                isGrupoDeRecursos: current.isGrupoDeRecursos || false,
                recursosDoGrupo: Array.isArray(current.recursosDoGrupo) ? current.recursosDoGrupo.map(r => extractId(r)) : [],
                grupoPertencente: extractId(current.grupoPertencente) || '',
                metadata: {
                    tipoItem: Array.isArray(current.metadata?.tipoItem) ? current.metadata.tipoItem : 
                             (current.metadata?.tipoItem ? [current.metadata.tipoItem] : ['interno']),
                    categoriaItem: current.metadata?.categoriaItem || 'locacao',
                    disponivelOnline: current.metadata?.disponivelOnline || false,
                    movel: current.metadata?.movel || false,
                    capacidade: current.metadata?.capacidade || 1,
                    caracteristicas: Array.isArray(current.metadata?.caracteristicas) ? current.metadata.caracteristicas : [],
                    ...current.metadata
                }
            };

            setRecurso(recursoAtualizado);
            setImageUpdateTrigger(prev => prev + 1);
        } else {
            setRecurso(initialState);
            setImagemPreview(DEFAULT_PLACEHOLDER_IMAGE);
        }
    }, [current]);

    const onChange = event => {
        const { name, value, type, checked } = event.target;
        
        if (errors[name]) {
            setErrors(previousErrors => ({ ...previousErrors, [name]: null }));
        }
        
        if (name.startsWith('metadata.')) {
            const metadataField = name.replace('metadata.', '');
            
            if (metadataField === 'tipoItem') {
                setRecurso(previousState => {
                    const currentArray = Array.isArray(previousState.metadata.tipoItem) ? [...previousState.metadata.tipoItem] : [];
                    let newArray;
                    
                    if (checked) {
                        if (!currentArray.includes(value)) {
                            newArray = [...currentArray, value];
                        } else {
                            newArray = currentArray;
                        }
                    } else {
                        newArray = currentArray.filter(item => item !== value);
                    }
                    
                    return {
                        ...previousState,
                        metadata: {
                            ...previousState.metadata,
                            tipoItem: newArray
                        }
                    };
                });
            } else {
                setRecurso(previousState => ({
                    ...previousState,
                    metadata: {
                        ...previousState.metadata,
                        [metadataField]: type === 'checkbox' ? checked : 
                                       (type === 'number' ? parseFloat(value) || 0 : value)
                    }
                }));
            }
        } else if (name === 'valorPorHora' || name === 'metadata.capacidade') {
            setRecurso(previousState => ({ ...previousState, [name]: parseFloat(value) || 0 }));
        } else if (type === 'checkbox') {
            setRecurso(previousState => ({ ...previousState, [name]: checked }));
        } else {
            setRecurso(previousState => ({ ...previousState, [name]: value }));
        }
    };

    const handleUnidadesPermitidasChange = (event) => {
        const { value, checked } = event.target;
        setRecurso(previousState => {
            const currentArray = Array.isArray(previousState.unidadesPermitidas) ? [...previousState.unidadesPermitidas] : [];
            let newArray;
            
            if (checked) {
                if (!currentArray.includes(value)) {
                    newArray = [...currentArray, value];
                } else {
                    newArray = currentArray;
                }
            } else {
                newArray = currentArray.filter(item => item !== value);
            }
            
            return {
                ...previousState,
                unidadesPermitidas: newArray
            };
        });
    };

    const onCheckboxChange = (event) => {
        const { name, value, checked } = event.target;
        const stateFieldName = name;
        setRecurso(previousState => {
            const currentArray = Array.isArray(previousState[stateFieldName]) ? [...previousState[stateFieldName]] : [];
            if (checked) {
                if (!currentArray.includes(value)) {
                    return { ...previousState, [stateFieldName]: [...currentArray, value] };
                }
            } else {
                return { ...previousState, [stateFieldName]: currentArray.filter(id => id !== value) };
            }
            return previousState;
        });
    };

    const adicionarRecursoAoGrupo = useCallback(() => {
        if (recursoSelecionadoParaGrupo && !recurso.recursosDoGrupo.includes(recursoSelecionadoParaGrupo)) {
            setRecurso(prev => ({ 
                ...prev, 
                recursosDoGrupo: [...prev.recursosDoGrupo, recursoSelecionadoParaGrupo] 
            }));
            setRecursoSelecionadoParaGrupo('');
        }
    }, [recursoSelecionadoParaGrupo, recurso.recursosDoGrupo]);

    const removerRecursoDoGrupo = useCallback((recursoId) => {
        setRecurso(prev => ({ 
            ...prev, 
            recursosDoGrupo: prev.recursosDoGrupo.filter(id => id !== recursoId) 
        }));
    }, []);

    const adicionarTag = useCallback(() => {
        if (novaTag.trim() && !recurso.tags.includes(novaTag.trim())) {
            setRecurso(prev => ({ ...prev, tags: [...prev.tags, novaTag.trim()] }));
            setNovaTag('');
        }
    }, [novaTag, recurso.tags]);

    const removerTag = useCallback((tagParaRemover) => {
        setRecurso(prev => ({ ...prev, tags: prev.tags.filter(tag => tag !== tagParaRemover) }));
    }, []);

    const handleTagKeyPress = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            adicionarTag();
        }
    }, [adicionarTag]);

    const handlePacoteChange = (index, field, value) => {
        const updatedPacotes = [...recurso.pacotesDePreco];
        let processedValue = value;
        
        if (['quantidade', 'valor', 'descontoPercentual'].includes(field)) {
            processedValue = field === 'descontoPercentual' ? 
                Math.min(100, Math.max(0, parseFloat(value) || 0)) : 
                parseFloat(value) || 0;
            
            if (field === 'quantidade' && processedValue < 1) {
                processedValue = 1;
            }
            
            if (field === 'valor' && processedValue <= 0) {
                processedValue = 0;
            }
        }
        
        updatedPacotes[index] = { 
            ...updatedPacotes[index], 
            [field]: processedValue 
        };
        
        setRecurso(previousState => ({ ...previousState, pacotesDePreco: updatedPacotes }));
        
        const errorKey = `pacote-${index}-${field}`;
        if (errors[errorKey]) {
            setErrors(previousErrors => ({ ...previousErrors, [errorKey]: null }));
        }
    };

    const addPacote = () => {
        setRecurso(previousState => ({
            ...previousState,
            pacotesDePreco: [...previousState.pacotesDePreco, {
                nome: `Pacote ${previousState.pacotesDePreco.length + 1}`,
                tipoDuracao: 'horas',
                quantidade: 1,
                valor: 0,
                descontoPercentual: 0,
                ativo: true,
                horaInicio: '',
                horaFim: ''
            }]
        }));
    };

    const removePacote = (index) => {
        setRecurso(previousState => ({
            ...previousState,
            pacotesDePreco: previousState.pacotesDePreco.filter((_, i) => i !== index)
        }));
        
        const newErrors = { ...errors };
        Object.keys(newErrors).forEach(key => {
            if (key.startsWith(`pacote-${index}`)) {
                delete newErrors[key];
            }
        });
        setErrors(newErrors);
    };

    const togglePacoteAtivo = (index) => {
        const updatedPacotes = [...recurso.pacotesDePreco];
        updatedPacotes[index] = { 
            ...updatedPacotes[index], 
            ativo: !updatedPacotes[index].ativo 
        };
        setRecurso(previousState => ({ ...previousState, pacotesDePreco: updatedPacotes }));
    };

    const adicionarCaracteristica = () => {
        if (novaCaracteristica.trim()) {
            setRecurso(previousState => ({
                ...previousState,
                metadata: {
                    ...previousState.metadata,
                    caracteristicas: [...(previousState.metadata.caracteristicas || []), novaCaracteristica.trim()]
                }
            }));
            setNovaCaracteristica('');
        }
    };

    const removerCaracteristica = (index) => {
        setRecurso(previousState => ({
            ...previousState,
            metadata: {
                ...previousState.metadata,
                caracteristicas: previousState.metadata.caracteristicas.filter((_, i) => i !== index)
            }
        }));
    };

    const handleCaracteristicaKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            adicionarCaracteristica();
        }
    };

    const validarPacotes = () => {
        const novosErros = {};
        
        if (recurso.tipoDePrecificacao === 'Por Pacote') {
            if (!recurso.pacotesDePreco || recurso.pacotesDePreco.length === 0) {
                novosErros.pacotes = 'Recursos com precificação por pacote devem ter pelo menos um pacote configurado.';
                return novosErros;
            }

            recurso.pacotesDePreco.forEach((pacote, index) => {
                if (!pacote.nome || !pacote.nome.trim()) {
                    novosErros[`pacote-${index}-nome`] = 'Nome do pacote é obrigatório';
                }
                
                if (!pacote.quantidade || pacote.quantidade <= 0) {
                    novosErros[`pacote-${index}-quantidade`] = 'Quantidade deve ser maior que zero';
                }
                
                if (!pacote.valor || pacote.valor <= 0) {
                    novosErros[`pacote-${index}-valor`] = 'Valor deve ser maior que zero';
                }
            });
        }
        
        return novosErros;
    };

    const validarHierarquia = () => {
        const novosErros = {};
        
        if (recurso.tipo === 'Maca ou Cadeira' && !recurso.recursoPai && !recurso.isGrupoDeRecursos) {
            novosErros.recursoPai = 'Recursos do tipo "Maca ou Cadeira" devem ter um recurso pai do tipo Sala, ou ser um grupo de recursos';
        }
        
        if (recurso.tipo === 'Equipamento' && recurso.metadata.movel && recurso.unidadesPermitidas.length === 0) {
            novosErros.unidadesPermitidas = 'Equipamentos móveis devem ter pelo menos uma unidade permitida';
        }
        
        return novosErros;
    };

    // ======================================================
    // ✅ FUNÇÕES DE RENDERIZAÇÃO (DEFINIDAS APÓS recurso)
    // ======================================================

    const renderCategoriasOptions = () => {
        if (loadingCategorias) return <option value="">Carregando categorias...</option>;
        if (categoriasError) return <option value="">Erro ao carregar categorias</option>;
        
        if (!categorias || categorias.length === 0) {
            return <option value="">Nenhuma categoria disponível. Crie categorias primeiro.</option>;
        }
        
        const options = [<option key="select" value="">Selecione uma categoria</option>];
        
        categorias.forEach(categoria => {
            options.push(
                <option key={extractId(categoria)} value={extractId(categoria)}>
                    {categoria.nome} {categoria.tipo && `(${categoria.tipo})`}
                </option>
            );
        });
        
        return options;
    };

    const renderUnidadesOptions = () => {
        if (loadingUnidades) return <option value="">Carregando unidades...</option>;
        if (unidadesError) return <option value="">Erro ao carregar unidades</option>;
        if (!unidades || unidades.length === 0) return <option value="">Nenhuma unidade encontrada</option>;
        
        const options = [<option key="select" value="">Selecione a Unidade</option>];
        
        unidades.forEach(unidade => {
            const localidade = unidade.endereco?.localidade || unidade.localidade || '';
            const uf = unidade.endereco?.uf || unidade.uf || '';
            const sufixoEndereco = localidade ? ` (${localidade}${uf ? `, ${uf}` : ''})` : '';

            options.push(
                <option key={extractId(unidade)} value={extractId(unidade)}>
                    {unidade.nome}{sufixoEndereco}
                </option>
            );
        });
        
        return options;
    };

    const renderUnidadesPermitidas = () => {
        if (loadingUnidades) return <p className="text-sm text-gray-500">Carregando unidades...</p>;
        if (unidadesError) return <p className="text-sm text-red-500">Erro ao carregar unidades</p>;
        if (!unidades || unidades.length === 0) return <p className="text-sm text-gray-500">Nenhuma unidade disponível</p>;
        
        return unidades.map(unidade => {
            const id = extractId(unidade);
            const localidade = unidade.endereco?.localidade || unidade.localidade || '';
            const uf = unidade.endereco?.uf || unidade.uf || '';
            const sufixoEndereco = localidade ? ` (${localidade}${uf ? `, ${uf}` : ''})` : '';

            return (
                <div key={id} className="flex items-center mb-2">
                    <input 
                        type="checkbox" 
                        id={`unidade-permitida-${id}`}
                        value={id} 
                        checked={recurso.unidadesPermitidas.includes(id)}
                        onChange={handleUnidadesPermitidasChange}
                        className="form-checkbox h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"
                    />
                    <label htmlFor={`unidade-permitida-${id}`} className="text-sm text-gray-700">
                        {unidade.nome}{sufixoEndereco}
                    </label>
                </div>
            );
        });
    };

    const getFieldError = (fieldName) => {
        return errors[fieldName];
    };

    const handleImageError = (e) => {
        console.log('❌ [IMAGE_ERROR] Falha ao carregar imagem, usando placeholder');
        e.target.src = DEFAULT_PLACEHOLDER_IMAGE;
    };

    const renderStatusParaGrupo = () => {
        if (!recurso) return null;
        
        if (recurso.isGrupoDeRecursos) {
            return (
                <div className="form-group">
                    <label htmlFor="status" className="form-label">Status do Grupo</label>
                    <select name="status" id="status" value={recurso.status} onChange={onChange} className="form-input">
                        <option value="Ativo">Ativo (Grupo disponível para uso)</option>
                        <option value="Inativo">Inativo (Grupo indisponível)</option>
                    </select>
                    <p className="form-hint">Grupos ativos podem ser usados por serviços que aceitam qualquer recurso do pool</p>
                </div>
            );
        }
        
        if (recurso.grupoPertencente) {
            return (
                <div className="form-group">
                    <label htmlFor="status" className="form-label">Status</label>
                    <select name="status" id="status" value={recurso.status} onChange={onChange} className="form-input">
                        <option value="Ativo">Ativo</option>
                        <option value="Inativo">Inativo</option>
                        <option value="Em Manutenção">Em Manutenção</option>
                        <option value="Bloqueado">Bloqueado</option>
                        <option value="Fora de Serviço">Fora de Serviço</option>
                    </select>
                    <div className="mt-2 p-2 bg-blue-50 rounded-md">
                        <p className="text-sm text-blue-700 flex items-center">
                            <Link size={14} className="mr-1" />
                            Este recurso pertence ao pool de recursos
                        </p>
                    </div>
                </div>
            );
        }
        
        return (
            <div className="form-group">
                <label htmlFor="status" className="form-label">Status</label>
                <select name="status" id="status" value={recurso.status} onChange={onChange} className="form-input">
                    <option value="Ativo">Ativo</option>
                    <option value="Inativo">Inativo</option>
                    <option value="Em Manutenção">Em Manutenção</option>
                    <option value="Bloqueado">Bloqueado</option>
                    <option value="Fora de Serviço">Fora de Serviço</option>
                </select>
            </div>
        );
    };

    const renderSeçãoGrupoDeRecursos = () => {
        if (!recurso?.isGrupoDeRecursos) return null;

        return (
            <div className="form-group full-width mt-4 p-4 border rounded-md bg-purple-50 border-purple-200">
                <label className="form-group-title block text-sm font-medium text-purple-800 mb-2">
                    <Layers size={16} className="inline mr-1" />
                    Recursos que compõem este grupo
                </label>
                <p className="text-sm text-purple-600 mb-3">
                    Selecione os recursos individuais que fazem parte deste grupo (ex: Cadeira 1, Cadeira 2, Cadeira 3...)
                </p>
                
                <div className="flex gap-2 mb-3">
                    <select 
                        value={recursoSelecionadoParaGrupo}
                        onChange={(e) => setRecursoSelecionadoParaGrupo(e.target.value)}
                        className="form-input flex-1"
                        disabled={loadingRecursosDisponiveis}
                    >
                        <option value="">Selecione um recurso para adicionar ao grupo</option>
                        {recursosDisponiveis.map(r => {
                            const rId = extractId(r);
                            if (recurso.recursosDoGrupo?.includes(rId)) return null;
                            return (
                                <option key={rId} value={rId}>
                                    {r.nome} ({r.tipo})
                                </option>
                            );
                        })}
                    </select>
                    <button 
                        type="button"
                        onClick={adicionarRecursoAoGrupo}
                        disabled={!recursoSelecionadoParaGrupo}
                        className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-md flex items-center gap-1 transition-colors disabled:opacity-50"
                    >
                        <PlusCircle size={16} />
                        Adicionar
                    </button>
                </div>

                {recurso.recursosDoGrupo?.length > 0 && (
                    <div className="mt-3">
                        <div className="flex flex-wrap gap-2">
                            {recurso.recursosDoGrupo.map((recursoId) => {
                                const recursoInfo = recursosDisponiveis.find(r => extractId(r) === recursoId);
                                return (
                                    <span 
                                        key={recursoId}
                                        className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm"
                                    >
                                        {recursoInfo?.nome || recursoId.substring(0, 8)}
                                        <button 
                                            type="button"
                                            onClick={() => removerRecursoDoGrupo(recursoId)}
                                            className="text-purple-600 hover:text-purple-800 transition-colors ml-1"
                                        >
                                            <XCircle size={14} />
                                        </button>
                                    </span>
                                );
                            })}
                        </div>
                        <p className="text-sm text-gray-500 mt-2">
                            Total de {recurso.recursosDoGrupo.length} recursos neste grupo
                        </p>
                    </div>
                )}
            </div>
        );
    };

    const renderCampoCapacidade = () => {
        if (!recurso) return null;
        if (recurso.tipo === 'Sala' && !recurso.isGrupoDeRecursos) {
            return (
                <div className="form-group">
                    <label htmlFor="capacidade" className="form-label flex items-center">
                        <Users size={16} className="mr-1" />
                        Capacidade (pessoas)
                    </label>
                    <input 
                        type="number" 
                        id="capacidade" 
                        name="metadata.capacidade" 
                        value={recurso.metadata?.capacidade || 1} 
                        onChange={onChange} 
                        className="form-input" 
                        min="1" 
                        placeholder="Ex: 4"
                    />
                    <p className="form-hint">Número máximo de pessoas que a sala comporta</p>
                </div>
            );
        }
        return null;
    };

    const renderSeçãoEquipamentoMovel = () => {
        if (!recurso) return null;
        if (recurso.tipo === 'Equipamento' && !recurso.isGrupoDeRecursos) {
            return (
                <div className="form-group full-width">
                    <div className="form-group-checkbox mb-4">
                        <input 
                            type="checkbox" 
                            id="movel" 
                            name="metadata.movel" 
                            checked={recurso.metadata?.movel || false} 
                            onChange={onChange} 
                            className="form-checkbox h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" 
                        />
                        <label htmlFor="movel" className="form-label flex items-center">
                            <MapPin size={16} className="mr-1" />
                            Equipamento Móvel
                        </label>
                        <p className="form-hint">Pode ser usado em múltiplas unidades</p>
                    </div>

                    {recurso.metadata?.movel && (
                        <div className="mt-4 p-4 border rounded-md bg-blue-50 border-blue-200">
                            <label className="form-group-title block text-sm font-medium text-gray-700 mb-2">
                                Unidades Permitidas*
                                {getFieldError('unidadesPermitidas') && (
                                    <span className="text-red-500 ml-2 text-sm font-normal">
                                        {getFieldError('unidadesPermitidas')}
                                    </span>
                                )}
                            </label>
                            <div className="checkbox-group max-h-40 overflow-y-auto border border-gray-300 p-3 rounded-md bg-white mt-2">
                                {renderUnidadesPermitidas()}
                            </div>
                            <p className="form-hint mt-2 text-sm text-gray-600">
                                Selecione as unidades onde este equipamento pode ser usado
                            </p>
                        </div>
                    )}
                </div>
            );
        }
        return null;
    };

    const renderCaracteristicas = () => {
        if (!recurso) return null;
        if (recurso.isGrupoDeRecursos) return null;
        
        return (
            <div className="form-group full-width">
                <label className="form-label">
                    Características
                </label>
                <div className="space-y-2">
                    <div className="flex gap-2">
                        <input 
                            type="text" 
                            value={novaCaracteristica}
                            onChange={(e) => setNovaCaracteristica(e.target.value)}
                            onKeyPress={handleCaracteristicaKeyPress}
                            className="form-input flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Ex: Ar condicionado, Wi-Fi, Projetor..."
                        />
                        <button 
                            type="button"
                            onClick={adicionarCaracteristica}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-1 transition-colors"
                        >
                            <PlusCircle size={16} />
                            Adicionar
                        </button>
                    </div>
                    <p className="form-hint text-sm text-gray-600">
                        Pressione Enter ou clique no botão para adicionar características
                    </p>
                    
                    {recurso.metadata?.caracteristicas && recurso.metadata.caracteristicas.length > 0 && (
                        <div className="mt-2">
                            <div className="flex flex-wrap gap-2">
                                {recurso.metadata.caracteristicas.map((caracteristica, index) => (
                                    <span 
                                        key={index}
                                        className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                                    >
                                        {caracteristica}
                                        <button 
                                            type="button"
                                            onClick={() => removerCaracteristica(index)}
                                            className="text-blue-600 hover:text-blue-800 transition-colors ml-1"
                                        >
                                            <XCircle size={14} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // ======================================================
    // ✅ RENDERIZAÇÃO DAS ABAS
    // ======================================================

    const renderAbaBasicos = () => {
        return (
            <fieldset className="form-section">
                <legend className="form-section-title">Detalhes do Recurso</legend>
                <div className="form-grid-container">
                    <div className="form-group">
                        <label htmlFor="nome" className="form-label">
                            Nome do Recurso*
                        </label>
                        <input 
                            type="text" 
                            id="nome" 
                            name="nome" 
                            value={recurso.nome} 
                            onChange={onChange} 
                            required 
                            className="form-input" 
                            placeholder="Ex: Laser Harmony, Sala 01, Maca 01..." 
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="categoria" className="form-label">
                            <Folder size={16} className="inline mr-1" />
                            Categoria*
                        </label>
                        <select 
                            id="categoria" 
                            name="categoria" 
                            value={recurso.categoria} 
                            onChange={onChange} 
                            required 
                            className={`form-input ${getFieldError('categoria') ? 'border-red-500' : ''}`}
                            disabled={loadingCategorias}
                        >
                            {renderCategoriasOptions()}
                        </select>
                        {getFieldError('categoria') && (
                            <p className="text-red-500 text-xs mt-1">{getFieldError('categoria')}</p>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="tipo" className="form-label">
                            Tipo*
                        </label>
                        <select 
                            id="tipo" 
                            name="tipo" 
                            value={recurso.tipo} 
                            onChange={onChange} 
                            required 
                            className="form-input"
                        >
                            {TIPOS_RECURSO.map(tipo => (
                                <option key={tipo.value} value={tipo.value}>
                                    {tipo.icon} {tipo.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <div className="form-group-checkbox">
                        <input 
                            type="checkbox" 
                            id="isGrupoDeRecursos" 
                            name="isGrupoDeRecursos" 
                            checked={recurso.isGrupoDeRecursos} 
                            onChange={onChange} 
                            className="form-checkbox"
                            disabled={isEditing && recurso.recursosDoGrupo?.length > 0}
                        />
                        <label htmlFor="isGrupoDeRecursos" className="form-label flex items-center">
                            <Layers size={16} className="mr-1" />
                            Este recurso é um GRUPO de recursos (Pool)
                        </label>
                        <p className="form-hint">
                            Marque se este recurso representa um grupo/pool de recursos (ex: "Todas as Cadeiras" agrupando Cadeira 1,2,3,4)
                        </p>
                    </div>
                    
                    {renderCampoCapacidade()}
                    
                    <div className="form-group full-width">
                        <label htmlFor="tags" className="form-label">
                            <Tag size={16} className="inline mr-1" />
                            Tags
                        </label>
                        <div className="flex gap-2 mb-2">
                            <input 
                                type="text" 
                                value={novaTag} 
                                onChange={(e) => setNovaTag(e.target.value)} 
                                onKeyPress={handleTagKeyPress} 
                                className="form-input flex-1" 
                                placeholder="Adicionar tag..." 
                            />
                            <button 
                                type="button" 
                                onClick={adicionarTag} 
                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-1 transition-colors" 
                                disabled={!novaTag.trim()}
                            >
                                <PlusCircle size={16} />
                                Adicionar
                            </button>
                        </div>
                        <p className="form-hint text-sm text-gray-600 mb-2">
                            Pressione Enter ou clique no botão para adicionar Tags
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {recurso.tags.map((tag, index) => (
                                <span key={index} className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                                    {tag}
                                    <button 
                                        type="button" 
                                        onClick={() => removerTag(tag)} 
                                        className="text-green-600 hover:text-green-800 transition-colors ml-1"
                                    >
                                        <XCircle size={14} />
                                    </button>
                                </span>
                            ))}
                            {recurso.tags.length === 0 && <span className="text-sm text-gray-500">Nenhuma tag adicionada</span>}
                        </div>
                    </div>
                    
                    {renderStatusParaGrupo()}
                    
                    {!recurso.isGrupoDeRecursos && renderCaracteristicas()}
                    
                    <div className="form-group full-width">
                        <label htmlFor="descricao" className="form-label">
                            Descrição
                        </label>
                        <textarea 
                            id="descricao" 
                            name="descricao" 
                            value={recurso.descricao} 
                            onChange={onChange} 
                            rows="3" 
                            className="form-input" 
                            placeholder="Descreva as características e finalidade deste recurso..."
                        ></textarea>
                    </div>
                    
                    <div className="form-group full-width">
                        <label className="form-section-title flex items-center">
                            <Image size={18} className="mr-2" />
                            Imagem Principal
                            {imagemPreview !== DEFAULT_PLACEHOLDER_IMAGE && (
                                <span className="ml-2 text-green-600 text-sm font-normal">
                                    ✓ Imagem vinculada
                                </span>
                            )}
                        </label>
                        <div className="flex items-center gap-4 mt-2 p-4 border rounded-lg bg-gray-50">
                            <img 
                                src={imagemPreview} 
                                alt="Imagem Principal" 
                                className="w-24 h-24 rounded-lg object-cover border shadow-sm"
                                onError={handleImageError}
                                key={imagemPreview}
                            />
                            <div className="flex-1">
                                <p className="font-medium text-gray-700">
                                    {imagemPreview !== DEFAULT_PLACEHOLDER_IMAGE 
                                        ? 'Imagem Atual do Recurso' 
                                        : 'Imagem do Recurso'
                                    }
                                </p>
                                <p className="form-hint mt-1">
                                    {imagemPreview !== DEFAULT_PLACEHOLDER_IMAGE 
                                        ? '✅ Esta imagem será preservada após salvar. Use o gerenciador de imagens para alterá-la.'
                                        : 'A imagem principal será definida através da função dedicada de gerenciamento de imagens após salvar o recurso.'
                                    }
                                </p>
                                {imagemPreview !== DEFAULT_PLACEHOLDER_IMAGE && (
                                    <div className="flex items-center gap-2 mt-2">
                                        <span className="text-green-600 text-sm font-medium">
                                            ✓ Imagem vinculada ao recurso
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            (URL: {imagemPreview.substring(0, 50)}...)
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {renderSeçãoGrupoDeRecursos()}
                </div>
            </fieldset>
        );
    };

    const renderAbaVinculos = () => {
        if (recurso.isGrupoDeRecursos) {
            return (
                <fieldset className="form-section">
                    <legend className="form-section-title">Informações do Grupo</legend>
                    <div className="form-grid-container">
                        <div className="form-group full-width">
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                <h4 className="font-medium text-purple-800 mb-2 flex items-center">
                                    <Layers size={16} className="mr-2" />
                                    Este é um Grupo de Recursos (Pool)
                                </h4>
                                <div className="text-purple-700 text-sm space-y-2">
                                    <p>✓ Grupos são usados para agrupar recursos intercambiáveis (ex: todas as cadeiras da barbearia).</p>
                                    <p>✓ Serviços que usam este grupo podem ser executados em QUALQUER recurso do grupo.</p>
                                    <p>✓ Os recursos individuais são gerenciados na aba "Dados Básicos" do formulário.</p>
                                    <p className="font-medium mt-2">💡 Exemplo: Crie um grupo "Cadeiras Principal" e adicione as 5 cadeiras da barbearia.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </fieldset>
            );
        }
        
        return (
            <fieldset className="form-section">
                <legend className="form-section-title">Vínculos e Hierarquia</legend>
                <div className="form-grid-container">
                    <div className="form-group">
                        <label htmlFor="unidade" className="form-label">
                            Unidade do Recurso*
                        </label>
                        <select 
                            id="unidade" 
                            name="unidade" 
                            value={recurso.unidade} 
                            onChange={onChange} 
                            required 
                            className="form-input" 
                            disabled={loadingUnidades}
                        >
                            {renderUnidadesOptions()}
                        </select>
                        {unidadesError && (
                            <p className="text-red-500 text-xs mt-1">
                                Erro ao carregar unidades: {unidadesError.message}
                            </p>
                        )}
                    </div>

                    {(recurso.tipo === 'Maca ou Cadeira' || recurso.tipo === 'Equipamento') && (
                        <div className="form-group">
                            <label htmlFor="recursoPai" className="form-label">
                                Recurso Pai 
                                {recurso.tipo === 'Maca ou Cadeira' && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            <select 
                                id="recursoPai" 
                                name="recursoPai" 
                                value={recurso.recursoPai} 
                                onChange={onChange} 
                                className={`form-input ${getFieldError('recursoPai') ? 'border-red-500' : ''}`}
                                required={recurso.tipo === 'Maca ou Cadeira'}
                                disabled={isLoadingRecursosPai}
                            >
                                <option value="">Selecione um recurso pai</option>
                                {isLoadingRecursosPai ? (
                                    <option disabled>Carregando recursos...</option>
                                ) : recursosPaiDisponiveis.length > 0 ? (
                                    recursosPaiDisponiveis.map(recursoPai => (
                                        <option key={extractId(recursoPai)} value={extractId(recursoPai)}>
                                            {recursoPai.nome} ({recursoPai.tipo}) - {recursoPai.unidade?.nome || 'Sem unidade'}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>
                                        {recurso.tipo === 'Maca ou Cadeira' 
                                            ? 'Nenhuma sala disponível para vincular a maca/cadeira' 
                                            : 'Nenhuma sala disponível para vincular equipamento'}
                                    </option>
                                )}
                            </select>
                            {getFieldError('recursoPai') && (
                                <p className="text-red-500 text-xs mt-1">{getFieldError('recursoPai')}</p>
                            )}
                        </div>
                    )}

                    {renderSeçãoEquipamentoMovel()}
                </div>
            </fieldset>
        );
    };

    const renderAbaPrecos = () => {
        if (recurso.isGrupoDeRecursos) {
            return (
                <fieldset className="form-section">
                    <legend className="form-section-title">Precificação para Locação</legend>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                        <p className="text-gray-500">Grupos de recursos não possuem precificação própria.</p>
                        <p className="text-sm text-gray-400 mt-1">A precificação é definida nos recursos individuais ou nos serviços que utilizam o grupo.</p>
                    </div>
                </fieldset>
            );
        }
        
        const showPrecosTab = recurso.disponivelPara.some(disponivel => disponivel.includes('LOCACAO'));
        
        if (!showPrecosTab) return null;

        return (
            <fieldset className="form-section">
                <legend className="form-section-title">Precificação para Locação</legend>
                <div className="form-grid-container">
                    <div className="form-group">
                        <label htmlFor="tipoDePrecificacao" className="form-label">
                            Modelo de Precificação
                        </label>
                        <select 
                            id="tipoDePrecificacao" 
                            name="tipoDePrecificacao" 
                            value={recurso.tipoDePrecificacao} 
                            onChange={onChange} 
                            className="form-input"
                        >
                            <option value="Por Hora">Por Hora</option>
                            <option value="Por Pacote">Por Pacote</option>
                            <option value="Sob Consulta">Sob Consulta</option>
                        </select>
                    </div>
                    
                    {recurso.tipoDePrecificacao === 'Por Hora' && (
                        <div className="form-group">
                            <label htmlFor="valorPorHora" className="form-label">
                                Valor por Hora ({currencySymbol})*
                            </label>
                            <input 
                                type="number" 
                                id="valorPorHora" 
                                name="valorPorHora" 
                                value={recurso.valorPorHora} 
                                onChange={onChange} 
                                className="form-input" 
                                min="0" 
                                step="0.01" 
                                required
                            />
                            <p className="form-hint">
                                Valor obrigatório para recursos disponíveis para locação
                            </p>
                        </div>
                    )}
                </div>

                {recurso.tipoDePrecificacao === 'Por Pacote' && (
                    <div className="mt-6">
                        <h4 className="form-group-title mb-2">Pacotes de Locação</h4>
                        {errors.pacotes && (
                            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                                <p className="text-red-700 text-sm font-medium">{errors.pacotes}</p>
                            </div>
                        )}
                        
                        {recurso.pacotesDePreco.map((pacote, index) => (
                            <div key={index} className={`grid grid-cols-1 md:grid-cols-6 gap-4 items-center mb-3 p-3 border rounded-md ${pacote.ativo ? 'bg-white' : 'bg-gray-100'}`}>
                                <div className="form-group md:col-span-2">
                                    <label htmlFor={`pacote-nome-${index}`} className="text-sm font-medium">
                                        Nome do Pacote*
                                    </label>
                                    <input 
                                        type="text" 
                                        id={`pacote-nome-${index}`} 
                                        value={pacote.nome} 
                                        onChange={(event) => handlePacoteChange(index, 'nome', event.target.value)} 
                                        className={`form-input mt-1 ${getFieldError(`pacote-${index}-nome`) ? 'border-red-500' : ''}`} 
                                        placeholder="Ex: Pacote 4 Horas" 
                                        required
                                    />
                                    {getFieldError(`pacote-${index}-nome`) && (
                                        <p className="text-red-500 text-xs mt-1">{getFieldError(`pacote-${index}-nome`)}</p>
                                    )}
                                </div>
                                
                                <div className="form-group">
                                    <label htmlFor={`pacote-tipo-${index}`} className="text-sm font-medium">
                                        Tipo
                                    </label>
                                    <select 
                                        id={`pacote-tipo-${index}`} 
                                        value={pacote.tipoDuracao || 'horas'} 
                                        onChange={(event) => handlePacoteChange(index, 'tipoDuracao', event.target.value)} 
                                        className="form-input mt-1"
                                    >
                                        <option value="horas">Horas</option>
                                        <option value="diaria">Diária</option>
                                        <option value="semanal">Semanal</option>
                                        <option value="mensal">Mensal</option>
                                    </select>
                                </div>
                                
                                <div className="form-group">
                                    <label htmlFor={`pacote-quantidade-${index}`} className="text-sm font-medium">
                                        Quantidade*
                                    </label>
                                    <input 
                                        type="number" 
                                        id={`pacote-quantidade-${index}`} 
                                        value={pacote.quantidade} 
                                        onChange={(event) => handlePacoteChange(index, 'quantidade', event.target.value)} 
                                        className={`form-input mt-1 ${getFieldError(`pacote-${index}-quantidade`) ? 'border-red-500' : ''}`} 
                                        min="1" 
                                        required
                                    />
                                    {getFieldError(`pacote-${index}-quantidade`) && (
                                        <p className="text-red-500 text-xs mt-1">{getFieldError(`pacote-${index}-quantidade`)}</p>
                                    )}
                                </div>
                                
                                <div className="form-group">
                                    <label htmlFor={`pacote-valor-${index}`} className="text-sm font-medium">
                                        Valor ({currencySymbol})*
                                    </label>
                                    <input 
                                        type="number" 
                                        id={`pacote-valor-${index}`} 
                                        value={pacote.valor} 
                                        onChange={(event) => handlePacoteChange(index, 'valor', event.target.value)} 
                                        className={`form-input mt-1 ${getFieldError(`pacote-${index}-valor`) ? 'border-red-500' : ''}`} 
                                        min="0" 
                                        step="0.01" 
                                        required
                                    />
                                    {getFieldError(`pacote-${index}-valor`) && (
                                        <p className="text-red-500 text-xs mt-1">{getFieldError(`pacote-${index}-valor`)}</p>
                                    )}
                                </div>
                                
                                <div className="form-group flex items-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => togglePacoteAtivo(index)}
                                        className={`px-3 py-2 rounded text-sm font-medium ${
                                            pacote.ativo
                                                ? 'bg-green-100 text-green-700 border border-green-200'
                                                : 'bg-gray-200 text-gray-600 border border-gray-300'
                                        }`}
                                    >
                                        {pacote.ativo ? 'Ativo' : 'Inativo'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => removePacote(index)}
                                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>

                                {pacote.tipoDuracao === 'diaria' && (
                                    <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 mt-1 border-t border-purple-100">
                                        <div className="form-group">
                                            <label htmlFor={`pacote-hora-inicio-${index}`} className="text-sm font-medium text-purple-800">
                                                Hora de Entrada (Check-in)
                                            </label>
                                            <input
                                                type="time"
                                                id={`pacote-hora-inicio-${index}`}
                                                value={pacote.horaInicio || ''}
                                                onChange={(event) => handlePacoteChange(index, 'horaInicio', event.target.value)}
                                                className="form-input mt-1"
                                            />
                                            <p className="form-hint mt-1">Deixe vazio para horário livre (definido no agendamento)</p>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor={`pacote-hora-fim-${index}`} className="text-sm font-medium text-purple-800">
                                                Hora de Saída (Check-out)
                                            </label>
                                            <input
                                                type="time"
                                                id={`pacote-hora-fim-${index}`}
                                                value={pacote.horaFim || ''}
                                                onChange={(event) => handlePacoteChange(index, 'horaFim', event.target.value)}
                                                className="form-input mt-1"
                                            />
                                            <p className="form-hint mt-1">Deixe vazio para horário livre (definido no agendamento)</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        
                        <button 
                            type="button" 
                            onClick={addPacote} 
                            className="action-button action-button-secondary mt-2"
                        >
                            <PlusCircle size={16} className="icon"/> Adicionar Pacote
                        </button>
                        <p className="form-hint mt-2">
                            Pacotes inativos não estarão disponíveis para seleção
                        </p>
                    </div>
                )}
            </fieldset>
        );
    };

    const renderAbaConfiguracoes = () => {
        if (recurso.isGrupoDeRecursos) {
            return (
                <fieldset className="form-section">
                    <legend className="form-section-title">Configurações do Grupo</legend>
                    <div className="form-grid-container">
                        <div className="form-group-checkbox">
                            <input 
                                type="checkbox" 
                                id="disponivelOnline" 
                                name="metadata.disponivelOnline" 
                                checked={recurso.metadata.disponivelOnline} 
                                onChange={onChange} 
                                className="form-checkbox" 
                            />
                            <label htmlFor="disponivelOnline" className="form-label">
                                Grupo disponível para agendamento online?
                            </label>
                            <p className="form-hint">
                                Se marcado, clientes poderão agendar serviços que usam este grupo de recursos
                            </p>
                        </div>
                        
                        <div className="form-group full-width">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h4 className="font-medium text-blue-800 mb-2 flex items-center">
                                    <Layers size={16} className="mr-2" />
                                    Exemplo de Grupo de Recursos - Barbearia:
                                </h4>
                                <div className="text-blue-700 text-sm space-y-1">
                                    <div className="flex items-start">
                                        <span className="text-green-600 mr-2">✓</span>
                                        <span>Crie um grupo chamado <strong>"Cadeiras Principal"</strong></span>
                                    </div>
                                    <div className="flex items-start">
                                        <span className="text-green-600 mr-2">✓</span>
                                        <span>Adicione as <strong>5 cadeiras individuais</strong> como recursos do grupo</span>
                                    </div>
                                    <div className="flex items-start">
                                        <span className="text-green-600 mr-2">✓</span>
                                        <span>Configure serviços como <strong>"Corte de Cabelo"</strong> para usar este grupo</span>
                                    </div>
                                    <div className="flex items-start">
                                        <span className="text-green-600 mr-2">✓</span>
                                        <span>O sistema aloca automaticamente qualquer cadeira disponível</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </fieldset>
            );
        }
        
        const hasUsoExterno = recurso.metadata.tipoItem.includes('externo');
        
        return (
            <fieldset className="form-section">
                <legend className="form-section-title">Configurações Avançadas</legend>
                <div className="form-grid-container">
                    <div className="form-group-checkbox">
                        <input 
                            type="checkbox" 
                            id="temAgendaPropria" 
                            name="temAgendaPropria" 
                            checked={recurso.temAgendaPropria} 
                            onChange={onChange} 
                            className="form-checkbox" 
                        />
                        <label htmlFor="temAgendaPropria" className="form-label">
                            Este recurso possui agenda própria?
                        </label>
                        <p className="form-hint">
                            Marque se o recurso (sala, equipamento, maca, veículo, etc.) tem horários próprios de disponibilidade.
                            As configurações detalhadas da agenda são gerenciadas em uma página específica.
                        </p>
                    </div>
                    
                    <div className="form-group">
                        <label className="form-group-title">
                            Disponível Para*
                        </label>
                        <div className="checkbox-group border p-3 rounded-md bg-gray-50">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <h4 className="font-medium text-sm mb-2 text-gray-700">
                                        <Building size={14} className="inline mr-1"/> Uso Interno
                                    </h4>
                                    <div className="space-y-2">
                                        <div className="flex items-center">
                                            <input 
                                                type="checkbox" 
                                                id="interno-servicos" 
                                                name="disponivelPara" 
                                                value="INTERNO_SERVICOS" 
                                                checked={recurso.disponivelPara.includes('INTERNO_SERVICOS')} 
                                                onChange={onCheckboxChange} 
                                                className="form-checkbox mr-2"
                                            />
                                            <label htmlFor="interno-servicos" className="text-sm">
                                                Serviços Internos
                                            </label>
                                        </div>
                                        <div className="flex items-center">
                                            <input 
                                                type="checkbox" 
                                                id="interno-locacao" 
                                                name="disponivelPara" 
                                                value="INTERNO_LOCACAO" 
                                                checked={recurso.disponivelPara.includes('INTERNO_LOCACAO')} 
                                                onChange={onCheckboxChange} 
                                                className="form-checkbox mr-2"
                                            />
                                            <label htmlFor="interno-locacao" className="text-sm">
                                                Locação Interna
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="font-medium text-sm mb-2 text-gray-700">
                                        <Globe size={14} className="inline mr-1"/> Uso Externo
                                    </h4>
                                    <div className="space-y-2">
                                        <div className="flex items-center">
                                            <input 
                                                type="checkbox" 
                                                id="externo-servicos" 
                                                name="disponivelPara" 
                                                value="EXTERNO_SERVICOS" 
                                                checked={recurso.disponivelPara.includes('EXTERNO_SERVICOS')} 
                                                onChange={onCheckboxChange} 
                                                className="form-checkbox mr-2 mt-1" 
                                            />
                                            <div>
                                                <label htmlFor="externo-servicos" className="font-medium flex items-center">
                                                    <Globe size={16} className="inline mr-2 text-green-600"/>
                                                    Serviços Externos
                                                </label>
                                                <p className="text-sm text-gray-600 ml-0 mt-1">
                                                    Para uso por clientes externos/outras clínicas via agendamento online
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <input 
                                                type="checkbox" 
                                                id="externo-locacao" 
                                                name="disponivelPara" 
                                                value="EXTERNO_LOCACAO" 
                                                checked={recurso.disponivelPara.includes('EXTERNO_LOCACAO')} 
                                                onChange={onCheckboxChange} 
                                                className="form-checkbox mr-2 mt-1" 
                                            />
                                            <label htmlFor="externo-locacao" className="text-sm">
                                                Locação Externa
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p className="form-hint mt-2">
                            Pode selecionar ambos! Exemplo: Laser para uso interno em serviços E locação externa para outras clínicas
                        </p>
                    </div>

                    <div className="form-group">
                        <label className="form-group-title">
                            Categoria Principal*
                        </label>
                        <div className="space-y-3 border p-3 rounded-md bg-gray-50">
                            <div className="flex items-center">
                                <input 
                                    type="radio" 
                                    id="categoria-servico" 
                                    name="metadata.categoriaItem" 
                                    value="servico" 
                                    checked={recurso.metadata.categoriaItem === 'servico'} 
                                    onChange={onChange} 
                                    className="form-radio mr-2" 
                                    required
                                />
                                <label htmlFor="categoria-servico" className="font-medium">
                                    Serviço
                                </label>
                            </div>
                            <p className="text-sm text-gray-600 ml-6 mb-2">
                                Para prestação de serviços (consultas, procedimentos)
                            </p>
                            
                            <div className="flex items-center">
                                <input 
                                    type="radio" 
                                    id="categoria-locacao" 
                                    name="metadata.categoriaItem" 
                                    value="locacao" 
                                    checked={recurso.metadata.categoriaItem === 'locacao'} 
                                    onChange={onChange} 
                                    className="form-radio mr-2" 
                                    required
                                />
                                <label htmlFor="categoria-locacao" className="font-medium">
                                    Locação
                                </label>
                            </div>
                            <p className="text-sm text-gray-600 ml-6">
                                Para aluguel/locação de espaços ou equipamentos
                            </p>
                        </div>
                        <p className="form-hint mt-2">
                            Define a categoria principal para relatórios e organização
                        </p>
                    </div>

                    <div className="form-group-checkbox">
                        <input 
                            type="checkbox" 
                            id="disponivelOnline" 
                            name="metadata.disponivelOnline" 
                            checked={recurso.metadata.disponivelOnline} 
                            onChange={onChange} 
                            className="form-checkbox" 
                        />
                        <label htmlFor="disponivelOnline" className="form-label">
                            Disponível para agendamento online?
                            {hasUsoExterno && !recurso.metadata.disponivelOnline && (
                                <span className="ml-2 text-orange-600 text-sm font-medium">
                                    (Recomendado para uso externo)
                                </span>
                            )}
                        </label>
                        <p className="form-hint">
                            Se marcado, clientes poderão agendar este recurso através do site/app
                        </p>
                    </div>

                    <div className="form-group full-width">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="font-medium text-blue-800 mb-2 flex items-center">
                                <Cpu size={16} className="mr-2" />
                                Exemplo Prático - Laser de Depilação:
                            </h4>
                            <div className="text-blue-700 text-sm space-y-1">
                                <div className="flex items-start">
                                    <span className="text-green-600 mr-2">✓</span>
                                    <span><strong>Uso Interno</strong> + <strong>Serviços Internos</strong> = Procedimentos na sua clínica</span>
                                </div>
                                <div className="flex items-start">
                                    <span className="text-green-600 mr-2">✓</span>
                                    <span><strong>Uso Interno</strong> + <strong>Locação Interna</strong> = Outros profissionais alugam na sua clínica</span>
                                </div>
                                <div className="flex items-start">
                                    <span className="text-green-600 mr-2">✓</span>
                                    <span><strong>Uso Externo</strong> + <strong>Locação Externa</strong> = Clientes levam o equipamento para suas clínicas</span>
                                </div>
                                <div className="mt-2 font-medium">
                                    ✅ <strong>Pode selecionar TODAS as opções!</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        );
    };

    const renderTabContent = () => {
        return (
            <form onSubmit={onSubmit} noValidate>
                <FormStepper currentStep={currentStep} steps={STEP_LABELS} />

                <div className="tab-content">
                    {STEP_IDS[currentStep - 1] === 'basicos' && renderAbaBasicos()}
                    {STEP_IDS[currentStep - 1] === 'vinculos' && renderAbaVinculos()}
                    {STEP_IDS[currentStep - 1] === 'precos' && renderAbaPrecos()}
                    {STEP_IDS[currentStep - 1] === 'configuracoes' && renderAbaConfiguracoes()}
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        onClick={onCloseForm}
                        className="action-button action-button-secondary"
                        disabled={loading}
                    >
                        <XCircle size={18} className="icon" /> Cancelar
                    </button>
                    <FormStepNavigation
                        currentStep={currentStep}
                        totalSteps={STEP_LABELS.length}
                        onBack={() => setCurrentStep((s) => s - 1)}
                        onNext={() => setCurrentStep((s) => s + 1)}
                        isSubmitting={loading}
                        submitLabel={isEditing ? 'Atualizar Recurso' : 'Salvar Recurso'}
                    />
                </div>
            </form>
        );
    };

    const onSubmit = async event => {
        event.preventDefault();
        
        setErrors({});

        if (!recurso.categoria) {
            setErrors({ categoria: 'Selecione uma categoria para o recurso' });
            setAlert('Por favor, selecione uma categoria para o recurso.', 'danger');
            goToTab('basicos');
            return;
        }

        if (!recurso.categoria.match(/^[0-9a-fA-F]{24}$/)) {
            setErrors({ categoria: 'Categoria selecionada é inválida' });
            setAlert('A categoria selecionada é inválida. Por favor, selecione uma categoria válida.', 'danger');
            goToTab('basicos');
            return;
        }

        if (!recurso.isGrupoDeRecursos && !recurso.unidade) {
            setAlert('Por favor, selecione uma unidade para o recurso.', 'danger');
            goToTab('vinculos');
            return;
        }

        if (!recurso.nome.trim()) {
            setAlert('O nome do recurso é obrigatório.', 'danger');
            goToTab('basicos');
            return;
        }

        if (recurso.isGrupoDeRecursos && recurso.recursosDoGrupo.length === 0) {
            setAlert('Grupos de recursos devem ter pelo menos um recurso associado.', 'danger');
            goToTab('basicos');
            return;
        }

        if (!recurso.isGrupoDeRecursos && (!recurso.metadata.tipoItem || recurso.metadata.tipoItem.length === 0)) {
            setAlert('Selecione pelo menos um tipo de uso (interno ou externo).', 'danger');
            goToTab('configuracoes');
            return;
        }

        if (!recurso.isGrupoDeRecursos && (!recurso.disponivelPara || recurso.disponivelPara.length === 0)) {
            setAlert('Selecione pelo menos uma opção em "Disponível Para".', 'danger');
            goToTab('configuracoes');
            return;
        }

        const errosHierarquia = validarHierarquia();
        if (Object.keys(errosHierarquia).length > 0 && !recurso.isGrupoDeRecursos) {
            setErrors(errosHierarquia);
            goToTab('vinculos');
            setAlert('Corrija os erros de hierarquia antes de salvar.', 'danger');
            return;
        }

        const errosPacotes = validarPacotes();
        if (Object.keys(errosPacotes).length > 0 && !recurso.isGrupoDeRecursos) {
            setErrors(errosPacotes);
            goToTab('precos');
            
            if (errosPacotes.pacotes) {
                setAlert(errosPacotes.pacotes, 'danger');
            } else {
                setAlert('Corrija os erros nos pacotes de preço antes de salvar.', 'danger');
            }
            return;
        }

        const disponivelLocacao = recurso.disponivelPara?.some(disponivel => disponivel.includes('LOCACAO'));
        if (disponivelLocacao && !recurso.isGrupoDeRecursos) {
            if (recurso.tipoDePrecificacao === 'Por Hora' && recurso.valorPorHora <= 0) {
                setAlert('Recursos disponíveis para locação devem ter valor por hora maior que zero.', 'danger');
                goToTab('precos');
                return;
            }
            
            if (recurso.tipoDePrecificacao === 'Por Pacote' && recurso.pacotesDePreco.length === 0) {
                setAlert('Recursos com precificação por pacote devem ter pelo menos um pacote configurado.', 'danger');
                goToTab('precos');
                return;
            }
        }

        try {
            const recursoDataToSend = { 
                ...recurso,
                account: accountId
            };
            
            if (recurso.imagemUrl && !recurso.imagemUrl.includes('data:image/svg')) {
                recursoDataToSend.imagemUrl = recurso.imagemUrl;
            }
            
            if (!recurso.isGrupoDeRecursos) {
                recursoDataToSend.disponivelPara = recurso.disponivelPara.filter(valor => 
                    VALORES_DISPONIBILIDADE.includes(valor)
                );
            } else {
                recursoDataToSend.disponivelPara = ['INTERNO_SERVICOS'];
                recursoDataToSend.tipoDePrecificacao = 'Por Hora';
                recursoDataToSend.valorPorHora = 0;
                recursoDataToSend.pacotesDePreco = [];
            }
            
            if (recursoDataToSend.pacotesDePreco && !recurso.isGrupoDeRecursos) {
                recursoDataToSend.pacotesDePreco = recursoDataToSend.pacotesDePreco.map(pacote => {
                    const tipoDuracao = inferirTipoDuracao(pacote.tipoDuracao, pacote.nome, pacote.descricao);
                    const entry = {
                        nome: pacote.nome.trim(),
                        tipoDuracao,
                        quantidade: Math.max(1, pacote.quantidade || 1),
                        valor: Math.max(0, pacote.valor || 0),
                        descontoPercentual: Math.min(100, Math.max(0, pacote.descontoPercentual || 0)),
                        ativo: pacote.ativo !== false,
                        ordem: pacote.ordem || 0
                    };
                    if (tipoDuracao === 'diaria') {
                        entry.horaInicio = pacote.horaInicio || '';
                        entry.horaFim = pacote.horaFim || '';
                    }
                    return entry;
                }).filter(pacote => pacote.ativo);
            }
            
            if (Array.isArray(recursoDataToSend.dependencias)) {
                recursoDataToSend.dependencias = recursoDataToSend.dependencias.map(d => extractId(d)).filter(Boolean);
            }

            if (Array.isArray(recursoDataToSend.unidadesPermitidas)) {
                recursoDataToSend.unidadesPermitidas = recursoDataToSend.unidadesPermitidas.map(u => extractId(u)).filter(Boolean);
            }
            
            if (!recursoDataToSend.recursoPai) {
                recursoDataToSend.recursoPai = null;
            } else {
                recursoDataToSend.recursoPai = extractId(recursoDataToSend.recursoPai);
            }
            
            if (!recurso.isGrupoDeRecursos) {
                recursoDataToSend.unidade = extractId(recursoDataToSend.unidade);
            } else {
                delete recursoDataToSend.unidade;
            }
            
            recursoDataToSend.categoria = extractId(recursoDataToSend.categoria);
            
            delete recursoDataToSend.criadoPor;
            delete recursoDataToSend.franquia;
            delete recursoDataToSend._id;
            delete recursoDataToSend.createdAt;
            delete recursoDataToSend.updatedAt;
            delete recursoDataToSend.id;
            delete recursoDataToSend.imagens;
            
            if (!recursoDataToSend.categoria || !recursoDataToSend.categoria.match(/^[0-9a-fA-F]{24}$/)) {
                throw new Error('ID de categoria inválido');
            }

            let result;
            const isEditMode = Boolean(isEditing && current && current._id);
            
            if (isEditMode) {
                result = await updateRecurso.mutateAsync({ 
                    id: current._id,
                    recursoData: recursoDataToSend 
                });
            } else {
                result = await addRecurso.mutateAsync(recursoDataToSend);
            }

            setAlert("Recurso salvo com sucesso!", "success");
            
            if (result.data) {
                const recursoAtualizado = {
                    ...recurso,
                    ...result.data,
                    imagemUrl: result.data.imagemUrl || recurso.imagemUrl
                };
                
                setRecurso(recursoAtualizado);
                setImageUpdateTrigger(prev => prev + 1);
            }
            
            if (onSaveSuccess) {
                onSaveSuccess(result.data);
            }
            
            if (!isEditing) {
                onCloseForm();
            }
        } catch (error) {
            console.error("❌ [SUBMIT] Erro no formulário de recurso:", error);
            const errorMessage = error.response?.data?.error || 
                               error.response?.data?.message || 
                               error.message ||
                               "Erro ao salvar recurso";
            setAlert(errorMessage, "danger");
        }
    };

    return (
        <div className="form-layout">
            <div className="form-header">
                <h3 className="form-title">
                    {isEditing ? `Editar Recurso: ${recurso.nome}` : 'Adicionar Novo Recurso'}
                </h3>
                <button 
                    type="button" 
                    onClick={onCloseForm} 
                    title="Fechar Formulário" 
                    className="form-close-button"
                >
                    <XCircle size={24} />
                </button>
            </div>

            {renderTabContent()}
        </div>
    );
};

RecursoForm.propTypes = {
    onCloseForm: PropTypes.func.isRequired,
    onSaveSuccess: PropTypes.func,
    current: PropTypes.object,
    accountId: PropTypes.string.isRequired,
};

export default RecursoForm;